import {
  BracketTree,
  isMatchTappable,
  ScoreBadge,
  slotRefLabel,
  ROW,
  GAP_HALF,
  LINE_OFFSET,
  LINE
} from './TournamentBracketTree'
import type {
  BracketMatch,
  BracketPlan,
  BracketSlot,
  DoubleEliminationPlan,
  LosersRound
} from './types'

/**
 * The winners bracket reuses the single-elimination tree (its first-round slots
 * are where the teams live — swaps happen there); the losers bracket and the
 * final stage only hold references before results.
 */
export function winnersAsBracketPlan(plan: DoubleEliminationPlan): BracketPlan {
  return {
    format: 'double_elimination',
    mainSize: plan.mainSize,
    thirdPlaceMatch: false,
    prelims: plan.prelims,
    rounds: plan.winnersRounds
  }
}

/** Knockout names imply "lose and you're out", which is false in the
 * qualifying stage — each bracket just numbers its rounds. */
function qualifyingRoundLabel(_roundOf: number, index: number): string {
  return `Rodada ${index + 1}`
}

const COL_W = 208
const COL_GAP = GAP_HALF * 2
const LANE_GAP = 32

interface TournamentDoubleElimViewProps {
  plan: DoubleEliminationPlan
  interactive: boolean
  selected: number | null
  onTap: (posIdx: number) => void
  /** Result launching: called when a tappable match is clicked. */
  onMatchTap?: (match: BracketMatch) => void
}

/**
 * Double elimination, club model: the qualifying stage (winners + losers
 * brackets, right-aligned so they end together — each qualifies 2) stacked
 * above the final stage, a plain 4-team single-elimination tree whose slots
 * reference the qualifying matches (Vencedor J5, Vencedor L4...).
 */
export function TournamentDoubleElimView({
  plan,
  interactive,
  selected,
  onTap,
  onMatchTap
}: TournamentDoubleElimViewProps) {
  return (
    // Single scroller (both axes) for the whole composition. Extra right
    // padding gives the qualifiers breathing room at the right edge.
    <div className="h-full w-full overflow-auto pb-2 pr-10">
      <div className="w-max space-y-8">
        <section>
          <PhaseTitle>Fase classificatória</PhaseTitle>
          {/* The winners/losers brackets don't converge (each qualifies 2), so
              they align at the LEFT — both start on the same first-round column;
              the shorter one simply ends earlier. */}
          <div className="w-max">
            <div className="flex">
              <div>
                <LaneTitle>Chave dos vencedores · classifica 2</LaneTitle>
                <BracketTree
                  plan={winnersAsBracketPlan(plan)}
                  interactive={interactive}
                  selected={selected}
                  onTap={onTap}
                  showChampion={false}
                  scroll={false}
                  roundLabelFn={qualifyingRoundLabel}
                  onMatchTap={onMatchTap}
                />
              </div>
            </div>
            <div style={{ height: LANE_GAP }} />
            <div className="flex">
              <div>
                <LaneTitle>Chave dos perdedores · classifica 2</LaneTitle>
                <LosersTree rounds={plan.losersRounds} onMatchTap={onMatchTap} />
              </div>
            </div>
          </div>
        </section>

        <section>
          <PhaseTitle>Fase final</PhaseTitle>
          <BracketTree
            plan={plan.finalPhase}
            interactive={false}
            selected={null}
            onTap={() => {}}
            scroll={false}
            matchPrefix="F"
            onMatchTap={onMatchTap}
          />
        </section>
      </div>
    </div>
  )
}

function PhaseTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="text-base font-semibold text-gray-900 mb-3">{children}</h3>
}

function LaneTitle({ children }: { children: React.ReactNode }) {
  return <div className="text-sm font-semibold text-gray-700 mb-2">{children}</div>
}

/** Flow layout for the losers bracket: a match with in-lane feeders sits at
 * their average Y (straight for one, elbow for two); pure-drop matches stack
 * below. Rows never overlap (minimum ROW apart). */
function layoutLosers(rounds: LosersRound[]): number[][] {
  const positions: number[][] = []
  rounds.forEach((round, roundIdx) => {
    const ys: number[] = []
    round.matches.forEach((match) => {
      const feederYs: number[] = []
      if (roundIdx > 0) {
        const prev = rounds[roundIdx - 1]
        for (const slot of [match.top, match.bottom]) {
          if (slot.kind === 'match_winner' && slot.matchSection === 'losers') {
            const srcIdx = prev.matches.findIndex((m) => m.number === slot.matchNumber)
            if (srcIdx >= 0) feederYs.push(positions[roundIdx - 1][srcIdx])
          }
        }
      }
      const wanted =
        feederYs.length > 0
          ? feederYs.reduce((a, b) => a + b, 0) / feederYs.length
          : ROW / 2
      const minY = ys.length > 0 ? ys[ys.length - 1] + ROW : ROW / 2
      ys.push(Math.max(wanted, minY))
    })
    positions.push(ys)
  })
  return positions
}

/** The losers bracket as a funnel: matches positioned by flow, connectors
 * drawn from the actual references (drops from the winners bracket show as
 * "Perdedor J#" labels). */
function LosersTree({
  rounds,
  onMatchTap
}: {
  rounds: LosersRound[]
  onMatchTap?: (match: BracketMatch) => void
}) {
  if (rounds.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        Sem partidas próprias — os perdedores da rodada classificam direto para a fase final.
      </p>
    )
  }

  const positions = layoutLosers(rounds)
  const colH = Math.max(ROW, ...positions.flat().map((y) => y + ROW / 2))
  const centerY = (roundIdx: number, matchIdx: number) => positions[roundIdx][matchIdx]

  return (
    <div className="flex items-start" style={{ gap: COL_GAP }}>
      {rounds.map((round, roundIdx) => (
        <div key={round.round} className="flex-shrink-0" style={{ width: COL_W }}>
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2 text-center h-4">
            Rodada {roundIdx + 1}
          </div>
          <div className="relative" style={{ height: colH }}>
            {round.matches.map((match, matchIdx) => {
              const y = centerY(roundIdx, matchIdx)
              const isLastRound = roundIdx === rounds.length - 1

              const incoming: { slotY: number; srcY: number }[] = []
              if (roundIdx > 0) {
                const prev = rounds[roundIdx - 1]
                const sides: { slot: BracketSlot; offset: number }[] = [
                  { slot: match.top, offset: -LINE_OFFSET },
                  { slot: match.bottom, offset: LINE_OFFSET }
                ]
                for (const { slot, offset } of sides) {
                  if (slot.kind === 'match_winner' && slot.matchSection === 'losers') {
                    const srcIdx = prev.matches.findIndex((m) => m.number === slot.matchNumber)
                    if (srcIdx >= 0) {
                      incoming.push({ slotY: y + offset, srcY: centerY(roundIdx - 1, srcIdx) })
                    }
                  }
                }
              }

              return (
                <div key={match.number}>
                  {incoming.map((line, i) => (
                    <span key={`c${match.number}-${i}`}>
                      <span
                        className="absolute h-px"
                        style={{
                          top: line.slotY,
                          left: -GAP_HALF,
                          width: GAP_HALF,
                          background: LINE
                        }}
                      />
                      {Math.abs(line.srcY - line.slotY) > 1 && (
                        <span
                          className="absolute w-px"
                          style={{
                            left: -GAP_HALF,
                            top: Math.min(line.srcY, line.slotY),
                            height: Math.abs(line.srcY - line.slotY),
                            background: LINE
                          }}
                        />
                      )}
                    </span>
                  ))}
                  {!isLastRound && (
                    <span
                      className="absolute h-px"
                      style={{ top: y, right: -GAP_HALF, width: GAP_HALF, background: LINE }}
                    />
                  )}
                  <div
                    className="absolute left-0 right-0"
                    style={{ top: y, transform: 'translateY(-50%)' }}
                  >
                    <div
                      onClick={onMatchTap && isMatchTappable(match) ? () => onMatchTap(match) : undefined}
                      className={`bg-white rounded-lg border border-gray-200 px-2 py-1.5 text-sm shadow-sm ${
                        onMatchTap && isMatchTappable(match)
                          ? 'cursor-pointer transition-colors hover:border-brand-primary/60'
                          : ''
                      }`}
                    >
                      <div className="text-[10px] font-semibold text-gray-400 mb-0.5">
                        L{match.number}
                      </div>
                      <div className="flex items-center">
                        <div className="flex-1 min-w-0">
                          <SlotText slot={match.top} winnerTeamId={match.winnerTeamId} />
                        </div>
                        <ScoreBadge
                          score={match.topScore}
                          walkover={match.walkover}
                          isWinner={match.winnerTeamId != null && match.top.team?.id === match.winnerTeamId}
                        />
                      </div>
                      <div className="border-t border-gray-100 my-0.5" />
                      <div className="flex items-center">
                        <div className="flex-1 min-w-0">
                          <SlotText slot={match.bottom} winnerTeamId={match.winnerTeamId} />
                        </div>
                        <ScoreBadge
                          score={match.bottomScore}
                          walkover={match.walkover}
                          isWinner={match.winnerTeamId != null && match.bottom.team?.id === match.winnerTeamId}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

function SlotText({ slot, winnerTeamId }: { slot: BracketSlot; winnerTeamId?: string | null }) {
  // A resolved slot shows its team regardless of kind (reference kinds carry
  // the propagated team once the source match is decided).
  if (slot.team) {
    if (winnerTeamId) {
      const isWinner = slot.team.id === winnerTeamId
      return (
        <div className={`truncate ${isWinner ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>
          {slot.team.name}
        </div>
      )
    }
    return <div className="text-gray-900 truncate">{slot.team.name}</div>
  }
  return <div className="text-gray-400 truncate">{slotRefLabel(slot, 'L')}</div>
}
